// src/components/layout/Header/NotificationDropdown.tsx

import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getIcon } from "@/components/icons/MenuIcons";
import { useStores } from "@/stores";
import useClickOutside from "@/hooks/useClickOutside";
import clsx from "clsx";

const NotificationDropdown = observer(() => {
  const { t } = useTranslation("commonNew");
  const [showDropdown, setShowDropdown] = useState(false);

	// Sử dụng custom hook
  const [dropdownRef, handleClickOutside] = useClickOutside<HTMLDivElement>(
    () => {
      setShowDropdown(false);
    }
  );

  // Kích hoạt click outside handling
  handleClickOutside(showDropdown);

  const { notificationStore } = useStores();
  const { isLoading, notifications, unreadNotificationCount } = notificationStore;

  // Load notifications khi dropdown mở
  useEffect(() => {
    notificationStore.fetchNotifications();
  }, []);

	return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative p-2 rounded-md hover:bg-gray-100 text-gray-700"
        onClick={() => setShowDropdown(!showDropdown)}
        title={t("notifications.title")}
      >
        {getIcon("Bell", { className: "h-5 w-5" })}
        {unreadNotificationCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full transform translate-x-1/2 -translate-y-1/2">
            {unreadNotificationCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className={clsx(
					'absolute right-0 mt-2',
					'w-80 bg-white rounded-md shadow-lg overflow-hidden z-20 border border-gray-200'
				)}>
          <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
            <h5 className="text-sm font-semibold text-gray-900 !py-3 m-0">
              {t("notifications.title")}
            </h5>
            {unreadNotificationCount > 0 && (
              <button
                className="text-xs text-indigo-600 hover:text-indigo-800"
                onClick={() => notificationStore.markAllNotificationsAsRead()}
              >
                {t("notifications.markAllAsRead")}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto py-1 w-full">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500 m-0">
                  {t("dataEmpty", {
                    topic: t("newNotification").toLocaleLowerCase(),
                  })}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={clsx(
										'px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer',
										!notification.isRead && 'bg-blue-50'
									)}
                  onClick={() => {
                    if (!notification.isRead) {
                      notificationStore.markNotificationAsRead(notification.id);
                    }
                  }}
                >
                  <div className="flex items-start">
                    <div
                      className={`flex-shrink-0 p-1 rounded-md ${getNotificationTypeColor(notification.type)}`}
                    >
                      {getNotificationTypeIcon(notification.type)}
                    </div>
                    <div className="ml-3 !flex flex-col !space-y-1">
                      <p className="text-sm font-medium m-0 text-gray-900">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 m-0 whitespace-pre-line">
                        {notification.content}
                      </p>
                      <p className="text-xs text-gray-400 m-0">
                        {formatTime(notification.createdAt)}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <div className="ml-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-600"></span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-2 border-t border-gray-100">
            <Link
              to="/notifications"
              className={clsx(
                "block text-sm text-center text-indigo-600 hover:text-indigo-800",
                "pointer-events-none !text-gray-300" // TODO REWORK
              )}
              onClick={() => setShowDropdown(false)}
            >
              {t("button.viewAllTopic", {
                topic: t("notifications.title").toLocaleLowerCase(),
              })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
});

// Helper functions
const getNotificationTypeColor = (type: string): string => {
  switch (type) {
    case "success":
      return "bg-green-100 text-green-500";
    case "warning":
      return "bg-yellow-100 text-yellow-500";
    case "error":
      return "bg-red-100 text-red-500";
    case "info":
    default:
      return "bg-blue-100 text-blue-500";
  }
};

const getNotificationTypeIcon = (type: string) => {
  switch (type) {
    case "success":
      return getIcon("Check", { className: "h-4 w-4" });
    case "warning":
      return getIcon("Warning", { className: "h-4 w-4" });
    case "error":
      return getIcon("Error", { className: "h-4 w-4" });
    case "info":
    default:
      return getIcon("Info", { className: "h-4 w-4" });
  }
};

const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Vừa xong";
  if (diffMins < 60) return `${diffMins} phút trước`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} giờ trước`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} ngày trước`;

  return date.toLocaleDateString();
};

export default NotificationDropdown;
